import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { Suggestion, SuggestionsResponse } from '../types'
import { apiClient } from '../services/api'
import { useApiAuth } from '../hooks/useApiAuth'

/**
 * App-level Discover feed state.
 *
 * The backend serves the feed stale-while-revalidate: `GET /suggestions` returns
 * the current (possibly stale/empty) feed immediately and reports `isRefreshing`
 * while a background recompute is in flight, advancing `refreshedAt` when newer
 * content lands (see packages/shared suggestion.ts).
 *
 * This provider lives above the router so a single poll loop drives both the
 * Discover page content AND a global "updating / updated" indicator that is
 * visible on every view — not just /discover. While a refresh is in flight it
 * polls `GET /suggestions` (a cheap cache read) on a bounded schedule and swaps
 * in fresh content the moment it appears.
 */

// Poll cadence while a background refresh is believed in-flight. Bounded: a feed
// with no fresh cache (a brand-new user below the save threshold, or a refresh
// that keeps failing) reports isRefreshing forever, so we stop after MAX_POLLS
// rather than spin the global indicator indefinitely.
const POLL_INTERVAL_MS = 4000
const MAX_POLLS = 15 // ~60s of tracking
const JUST_REFRESHED_MS = 5000 // how long the "updated" confirmation lingers
// Min gap before a tab refocus re-checks the feed, so rapid tab switching doesn't
// spam GET /suggestions (each stale hit can trigger a paid recompute server-side).
const FOCUS_REFETCH_MIN_MS = 15000

interface DiscoverRefreshValue {
  suggestions: Suggestion[]
  /** True until the first fetch resolves (no content shown yet). */
  loading: boolean
  /** A background refresh is in flight and we are actively polling for results. */
  isRefreshing: boolean
  /** Briefly true right after fresh content lands, to confirm completion. */
  justRefreshed: boolean
  error: string | null
  /** Re-read the feed now (re-reads cache; the server never force-recomputes). */
  refresh: () => void
  /** Optimistically drop a suggestion locally (after save/dismiss). */
  removeSuggestion: (url: string) => void
}

const DiscoverRefreshContext = createContext<DiscoverRefreshValue | null>(null)

export function useDiscoverRefresh(): DiscoverRefreshValue {
  const ctx = useContext(DiscoverRefreshContext)
  if (!ctx) {
    throw new Error('useDiscoverRefresh must be used within a DiscoverRefreshProvider')
  }
  return ctx
}

export function DiscoverRefreshProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth0()
  const { ready: authReady } = useApiAuth()

  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [loading, setLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [justRefreshed, setJustRefreshed] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Poll bookkeeping (refs so the loop isn't recreated and closures stay fresh).
  const refreshedAtRef = useRef<string | null>(null)
  const sawRefreshingRef = useRef(false)
  const pollCountRef = useRef(0)
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const justRefreshedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastFetchAtRef = useRef(0) // epoch ms of the last GET, for focus-refetch throttling

  const clearPollTimer = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }, [])

  const flagJustRefreshed = useCallback(() => {
    setJustRefreshed(true)
    if (justRefreshedTimerRef.current) clearTimeout(justRefreshedTimerRef.current)
    justRefreshedTimerRef.current = setTimeout(() => setJustRefreshed(false), JUST_REFRESHED_MS)
  }, [])

  // One fetch + decide whether to keep polling. Self-reschedules via setTimeout.
  const poll = useCallback(async () => {
    lastFetchAtRef.current = Date.now()
    let res: SuggestionsResponse
    try {
      res = await apiClient.getSuggestions()
    } catch {
      // Network error: keep whatever content we have, stop the indicator, surface
      // an error only if we have nothing to show.
      setLoading((wasLoading) => {
        if (wasLoading) setError("Couldn't load suggestions — check your connection")
        return false
      })
      setIsRefreshing(false)
      clearPollTimer()
      return
    }

    setError(null)
    setSuggestions(res.suggestions)
    setLoading(false)

    const advanced = res.refreshedAt != null && res.refreshedAt !== refreshedAtRef.current
    refreshedAtRef.current = res.refreshedAt
    if (res.isRefreshing) sawRefreshingRef.current = true

    if (res.isRefreshing && pollCountRef.current < MAX_POLLS) {
      setIsRefreshing(true)
      pollCountRef.current += 1
      pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS)
      return
    }

    // Terminal: refresh finished, or we hit the poll ceiling and gave up.
    setIsRefreshing(false)
    clearPollTimer()
    if (sawRefreshingRef.current && !res.isRefreshing && advanced) {
      flagJustRefreshed() // a tracked refresh actually produced newer content
    }
    sawRefreshingRef.current = false
  }, [clearPollTimer, flagJustRefreshed])

  // Start a fresh poll cycle from the top.
  const startCycle = useCallback(() => {
    clearPollTimer()
    pollCountRef.current = 0
    sawRefreshingRef.current = false
    poll()
  }, [clearPollTimer, poll])

  // Kick off once the user is authenticated and the API token is injected.
  useEffect(() => {
    if (!isAuthenticated || !authReady) return
    startCycle()
    return clearPollTimer
  }, [isAuthenticated, authReady, startCycle, clearPollTimer])

  // Re-check the feed when the tab regains focus / becomes visible, so returning
  // to the app surfaces a refresh that went stale while it was backgrounded —
  // without any manual action. Throttled by FOCUS_REFETCH_MIN_MS.
  useEffect(() => {
    if (!isAuthenticated || !authReady) return
    function onFocus() {
      if (document.visibilityState !== 'visible') return
      if (Date.now() - lastFetchAtRef.current < FOCUS_REFETCH_MIN_MS) return
      startCycle()
    }
    document.addEventListener('visibilitychange', onFocus)
    window.addEventListener('focus', onFocus)
    return () => {
      document.removeEventListener('visibilitychange', onFocus)
      window.removeEventListener('focus', onFocus)
    }
  }, [isAuthenticated, authReady, startCycle])

  // Clear the lingering "updated" timer on unmount.
  useEffect(
    () => () => {
      if (justRefreshedTimerRef.current) clearTimeout(justRefreshedTimerRef.current)
    },
    []
  )

  const refresh = useCallback(() => {
    if (!isAuthenticated || !authReady) return
    startCycle()
  }, [isAuthenticated, authReady, startCycle])

  const removeSuggestion = useCallback((url: string) => {
    setSuggestions((prev) => prev.filter((s) => s.url !== url))
  }, [])

  const value: DiscoverRefreshValue = {
    suggestions,
    loading,
    isRefreshing,
    justRefreshed,
    error,
    refresh,
    removeSuggestion,
  }

  return (
    <DiscoverRefreshContext.Provider value={value}>
      {children}
      {isAuthenticated && (
        <DiscoverRefreshIndicator isRefreshing={isRefreshing} justRefreshed={justRefreshed} />
      )}
    </DiscoverRefreshContext.Provider>
  )
}

/**
 * Global, view-independent indicator. Fixed to the viewport so it shows whether
 * the user is on Saved, Settings, an article, or Discover itself.
 */
function DiscoverRefreshIndicator({
  isRefreshing,
  justRefreshed,
}: {
  isRefreshing: boolean
  justRefreshed: boolean
}) {
  if (!isRefreshing && !justRefreshed) return null

  if (isRefreshing) {
    return (
      <div className="discover-toast" role="status" aria-live="polite">
        <div className="spinner discover-toast-spinner" />
        <span>Updating Discover…</span>
      </div>
    )
  }

  // justRefreshed
  return (
    <Link
      to="/discover"
      className="discover-toast discover-toast-done"
      role="status"
      aria-live="polite"
    >
      <span className="discover-toast-check" aria-hidden="true">
        ✓
      </span>
      <span>Discover updated</span>
    </Link>
  )
}
